// src/services/tbToken.js
//
// SINGLE source of truth for the ThingsBoard access token across the entire
// kiosk app. There is exactly ONE access token and ONE refresh token in the
// process at any moment. Every TB-bound caller — occupancy, swaas-score,
// telemetry, feedback→n8n, task resolve, mobile-qr — reads the live token via
// getAccessToken()/ensureFresh(). Nobody keeps a private copy that can go
// stale, and nobody runs its own refresh.
//
// Why this exists (2026-05-23): the app had multiple independent token copies
// (props threaded through components) and multiple refresh paths (useTokenRefresh
// AND ResolveTaskModal's own /api/refresh-token). That produced stale tokens and
// concurrent-refresh races → the /api/occupancy + /api/swaas-score 401 storms.
//
// Design:
//  - State at module scope (one instance; survives React StrictMode remounts).
//    Mirrored to localStorage + the Android native bridge so a WebView reload
//    restores the token instead of re-running /api/ref/validate.
//  - ensureFresh() PROACTIVELY refreshes when the JWT is within REFRESH_SKEW_MS
//    of expiry, BEFORE the token is used — so octacle never sees an expired
//    token and never returns 401. This removes the expiry *gap* rather than
//    reacting to 401s after the fact.
//  - refresh() is single-flight: concurrent callers await the same promise, so
//    N callers hitting expiry at once cause ONE refresh, not N racing ones.

const LS_ACCESS = 'tb_access_token';
const LS_REFRESH = 'tb_refresh_token';
const REFRESH_SKEW_MS = 60 * 1000; // refresh when <60s to expiry (proactive)
const REFRESH_TIMEOUT_MS = 8000;

let accessToken = null;
let refreshToken = null;
let apiBase = '';
let inFlight = null; // single-flight refresh promise
const listeners = new Set();

// ── persistence ───────────────────────────────────────────────────────────
function loadFromStorage() {
  try {
    accessToken = localStorage.getItem(LS_ACCESS) || null;
    refreshToken = localStorage.getItem(LS_REFRESH) || null;
  } catch {
    /* private mode / storage disabled — run in-memory only */
  }
}
loadFromStorage();

function persist() {
  try {
    if (accessToken) localStorage.setItem(LS_ACCESS, accessToken);
    if (refreshToken) localStorage.setItem(LS_REFRESH, refreshToken);
  } catch {
    /* ignore */
  }
  // Mirror to the native app (used by the gateway BLE flow + offline restore).
  try {
    if (accessToken && typeof window.AndroidDevice?.setTbToken === 'function') {
      window.AndroidDevice.setTbToken(accessToken);
    }
  } catch {
    /* ignore */
  }
}

function emit() {
  for (const cb of listeners) {
    try {
      cb(accessToken);
    } catch {
      /* a bad listener must not break token propagation */
    }
  }
}

// ── jwt exp ────────────────────────────────────────────────────────────────
export function decodeExpMs(jwt) {
  if (!jwt || typeof jwt !== 'string') return null;
  const parts = jwt.split('.');
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(b64 + pad));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isExpired(jwt, skewMs) {
  const exp = decodeExpMs(jwt);
  if (exp == null) return false; // non-JWT: can't tell, assume usable
  return Date.now() >= exp - skewMs;
}

// ── public API ──────────────────────────────────────────────────────────────

// Seed from /api/ref/validate on first load. Idempotent: only adopts a token
// that differs from what we already hold. After seeding, the singleton owns the
// lifecycle (refresh/persist).
export function initTbToken({ accessToken: a, refreshToken: r, apiBase: base } = {}) {
  if (base) apiBase = base;
  let changed = false;
  // CRITICAL: this is called from a hook body on every render with the ORIGINAL
  // /api/ref/validate token. Adopt a seed ONLY on first load, or when it is
  // strictly newer (by exp) than what we hold — otherwise a re-render would
  // clobber a token the singleton has already refreshed, reintroducing the very
  // stale-token bug this singleton exists to remove. A re-validate that mints a
  // genuinely fresher pair is still adopted (newer exp).
  if (a && a !== accessToken) {
    const curExp = decodeExpMs(accessToken);
    const seedExp = decodeExpMs(a);
    // Adopt when: no current token, OR the current token isn't a dateable JWT
    // (can't trust it — prefer the fresh validate seed), OR the seed is a newer
    // JWT than the current one.
    if (!accessToken || curExp === null || (seedExp !== null && seedExp > curExp)) {
      accessToken = a;
      changed = true;
    }
  }
  // Refresh tokens aren't comparable JWTs; adopt one only alongside an adopted
  // access token (paired) or when we hold none yet.
  if (r && r !== refreshToken && (changed || !refreshToken)) {
    refreshToken = r;
    changed = true;
  }
  if (changed) {
    persist();
    emit();
  }
}

export function getAccessToken() {
  return accessToken;
}

export function getRefreshToken() {
  return refreshToken;
}

export function getExpMs() {
  return decodeExpMs(accessToken);
}

// Single-flight refresh. Concurrent callers share one in-flight request.
export function refresh() {
  if (inFlight) return inFlight;
  if (!refreshToken || !apiBase) return Promise.resolve(accessToken);
  inFlight = (async () => {
    try {
      const res = await fetch(`${apiBase}/api/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
      });
      if (!res.ok) throw new Error(`refresh HTTP ${res.status}`);
      const newAccess = res.headers.get('X-TB-Token');
      const newRefresh = res.headers.get('X-TB-Refresh-Token');
      if (newAccess) accessToken = newAccess;
      if (newRefresh) refreshToken = newRefresh;
      if (newAccess || newRefresh) {
        persist();
        emit();
      }
      return accessToken;
    } catch (e) {
      // Keep the current token; the caller's request will fail and the next
      // ensureFresh() will retry. We do NOT loop here — single-flight + the
      // proactive skew mean a transient refresh failure self-heals.
      console.warn('[tbToken] refresh failed:', e?.message || e);
      return accessToken;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

// Return a token guaranteed valid at call time, refreshing proactively when it
// is within REFRESH_SKEW_MS of expiry. THIS is what every TB caller should use.
export async function ensureFresh() {
  if (!accessToken) return null;
  if (isExpired(accessToken, REFRESH_SKEW_MS)) {
    await refresh();
  }
  return accessToken;
}

// Subscribe to token changes (WebSocket reconnect, React re-render). Returns an
// unsubscribe fn.
export function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
